import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { User } from '../api/types';
import { DeleteAccountModal } from '../features/auth/DeleteAccountModal';
import { EditProfileModal } from '../features/auth/EditProfileModal';
import { useT, type Key } from '../i18n';
import { ConfirmModal } from './ConfirmModal';

interface Props {
  user: User;
  onLogout: () => void;
}

const ROLE_KEY: Record<string, Key> = {
  user: 'roleUser',
  manager: 'roleManager',
  admin: 'roleAdmin',
  supervisor: 'roleSupervisor',
};

/** "Uma User" -> "UU"; a single name -> its first letter. */
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

/** The avatar in the header: account details, delete, and sign out. */
export function UserMenu({ user, onLogout }: Props) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Signing out is one tap from the avatar and costs a full sign-in to undo,
  // so it asks first — the same guard the manager app grew.
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // A menu that only closes on its own items is a trap — close on an outside
  // click and on Escape too.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="avatar"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('menuAccount', { name: user.name })}
      >
        {initials(user.name)}
      </button>

      {open && (
        <div className="menu-panel" role="menu">
          <div className="menu-head">
            <span className="avatar lg" aria-hidden="true">
              {initials(user.name)}
            </span>
            <div className="menu-id">
              <b>{user.name}</b>
              <span className="muted">{user.email || user.phone}</span>
              <span className="pill">{ROLE_KEY[user.role] ? t(ROLE_KEY[user.role]) : user.role}</span>
            </div>
          </div>

          {/* The dashboard is no longer a header tab, so this is the only way
              back to it once a manager navigates away. A supervisor gets the
              same link — it opens their calendar-only view. */}
          {(user.role === 'manager' || user.role === 'supervisor') && (
            <Link className="menu-item" role="menuitem" to="/manager" onClick={() => setOpen(false)}>
              <i className="ri-briefcase-line" />{' '}
              {user.role === 'supervisor' ? t('menuBookingCalendar') : t('menuManagerDashboard')}
            </Link>
          )}

          {/* Explore's "saved only" filter is the favourites list. */}
          <Link className="menu-item" role="menuitem" to="/venues?saved=1" onClick={() => setOpen(false)}>
            <i className="ri-heart-3-line" /> {t('menuFavorites')}
          </Link>

          <button
            className="menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setEditing(true);
            }}
          >
            <i className="ri-user-settings-line" /> {t('menuEditDetails')}
          </button>

          <button
            className="menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setConfirmingLogout(true);
            }}
          >
            <i className="ri-logout-box-r-line" /> {t('menuLogout')}
          </button>

          <button
            className="menu-item danger"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setDeleting(true);
            }}
          >
            <i className="ri-delete-bin-line" /> {t('menuDeleteAccount')}
          </button>
        </div>
      )}

      {editing && <EditProfileModal user={user} onClose={() => setEditing(false)} />}
      {deleting && <DeleteAccountModal user={user} onClose={() => setDeleting(false)} />}
      {confirmingLogout && (
        <ConfirmModal
          title={t('menuLogoutConfirmTitle')}
          body={t('menuLogoutConfirmBody')}
          confirmLabel={t('menuLogoutConfirmAction')}
          danger
          onConfirm={() => {
            setConfirmingLogout(false);
            onLogout();
          }}
          onClose={() => setConfirmingLogout(false)}
        />
      )}
    </div>
  );
}
